import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Paper,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  CircularProgress,
  DialogContentText,
  Link,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Favorite as FavoriteIcon, Share as ShareIcon, Email as EmailIcon, Phone as PhoneIcon, ContentCopy as ContentCopyIcon, WhatsApp as WhatsAppIcon, Star as StarIcon, DeleteForever as DeleteForeverIcon, PauseCircle as PauseCircleIcon, PlayCircle as PlayCircleIcon, Person as PersonIcon, Verified as VerifiedIcon, AddPhotoAlternate as AddPhotoAlternateIcon } from '@mui/icons-material';
import instance from '../utils/axiosConfig.js';
import moment from 'moment';
import 'moment/locale/es';
import { auth } from '../utils/auth.js';
import { CATEGORY_HIERARCHY, getQualitiesForCategory } from '../constants/categories.js';
import LoadingScreen from '../components/LoadingScreen';
import WarningIcon from '@mui/icons-material/Warning';
import LdPlaceHolder from '../assets/ldplaceholder.png';
import {UNIT_LABELS_SINGULAR, UNIT_LABELS_PLURAL} from '../utils/units.js';
import { Fade } from '@mui/material';
moment.locale('es');

// const UNIT_LABELS_SINGULAR = {
//   'unidad': 'unidad',
//   'kg': 'kilogramo',
//   'lt': 'litro',
//   'm2': 'metro cuadrado',
//   'mt': 'metro',
//   'cm': 'centímetro',
//   'docena': 'docena',
//   'caja': 'caja',
//   'pack': 'pack',
//   'hectarea': 'hectárea'
// };

// const UNIT_LABELS_PLURAL = {
//   'unidad': 'unidades',
//   'kg': 'kilogramos',
//   'lt': 'litros',
//   'm2': 'metros cuadrados',
//   'mt': 'metros',
//   'cm': 'centímetros',
//   'docena': 'docenas',
//   'caja': 'cajas',
//   'pack': 'packs',
//   'hectarea': 'hectáreas'
// };

const Divider = () => (
  <Box
    sx={{
      my: 2,  // Increased vertical margin
      width: '100%', // Ensure full width
      height: '1px',
      background: 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(200,200,200,0.6) 50%, rgba(0,0,0,0) 100%)',
      borderRadius: '1px',
      display: 'block', // Ensure it displays as block element
      position: 'relative', // Add positioning context
    }}
  />
);

const getCategoryPath = (category) => {
  if (!category) return [];
  
  // Check if it's a main category
  if (CATEGORY_HIERARCHY[category]) {
    return [category];
  }

  // Look for the category in subcategories
  for (const [mainCat, data] of Object.entries(CATEGORY_HIERARCHY)) {
    for (const [subCat, items] of Object.entries(data.subcategories)) {
      if (subCat === category || items.includes(category)) {
        if (items.includes(category)) {
          return [mainCat, subCat, category];
        }
        return [mainCat, subCat];
      }
    }
  }

  return [category]; // Return single category if not found in hierarchy
};

const EditDialog = ({ open, onClose, field, value, productId, onUpdate }) => {
  const [localValue, setLocalValue] = useState(value);
  const [priceBreaks, setPriceBreaks] = useState([]);
  const [percentageAdjustment, setPercentageAdjustment] = useState('');
  const [includeBasePrice, setIncludeBasePrice] = useState(false);
  const [newBasePrice, setNewBasePrice] = useState(null);

  useEffect(() => {
    if (field === 'basePrice' || field === 'availableAmount') {
      setLocalValue(value.toString());
    } else if (field === 'priceBreaks') {
      setPriceBreaks(value || []);
    } else {
      setLocalValue(value);
    }
  }, [value, field]);

  const handleAddPriceBreak = () => {
    setPriceBreaks([...priceBreaks, { minQuantity: '', price: '' }]);
  };

  const handleRemovePriceBreak = (index) => {
    setPriceBreaks(priceBreaks.filter((_, i) => i !== index));
  };

  const handlePriceBreakChange = (index, field, value) => {
    const newPriceBreaks = [...priceBreaks];
    newPriceBreaks[index] = {
      ...newPriceBreaks[index],
      [field]: value ? Number(value) : ''
    };
    setPriceBreaks(newPriceBreaks);
  };

  const handlePercentageAdjustment = async () => {
    if (!percentageAdjustment || isNaN(percentageAdjustment)) return;
    
    const percentage = parseFloat(percentageAdjustment);
    
    try {
      // Update price breaks
      const newPriceBreaks = priceBreaks.map(pb => ({
        ...pb,
        price: pb.price ? Number((pb.price * (1 + percentage / 100)).toFixed(2)) : ''
      }));
      setPriceBreaks(newPriceBreaks);

      // If checkbox is checked, calculate new base price but don't update yet
      if (includeBasePrice) {
        const response = await instance.get(`/api/products/${productId}`);
        const currentBasePrice = response.data.basePrice;
        const calculatedNewBasePrice = Number((currentBasePrice * (1 + percentage / 100)).toFixed(2));
        setNewBasePrice(calculatedNewBasePrice);
      }

      setPercentageAdjustment(''); // Reset the input
    } catch (error) {
      console.error('Error updating prices:', error);
    }
  };

  const handleSave = async () => {
    try {
      let valueToSend = localValue;
      
      if (field === 'basePrice' || field === 'availableAmount') {
        valueToSend = Number(localValue);
      } else if (field === 'priceBreaks') {
        // Filter out incomplete price breaks and sort by quantity
        valueToSend = priceBreaks
          .filter(pb => pb.minQuantity && pb.price)
          .sort((a, b) => a.minQuantity - b.minQuantity);
      }

      // If we're updating price breaks and includeBasePrice is checked
      if (field === 'priceBreaks' && includeBasePrice && newBasePrice !== null) {
        await instance.put(`/api/products/${productId}`, {
          priceBreaks: valueToSend,
          basePrice: newBasePrice
        });
      } else {
        await instance.put(`/api/products/${productId}`, {
          [field]: valueToSend
        });
      }

      const response = await instance.get(`/api/products/${productId}`);
      onUpdate(response.data);
      onClose();
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Error al actualizar el producto');
    }
  };

  const renderEditContent = () => {
    if (field === 'priceBreaks') {
      return (
        <Box sx={{ width: '100%', mt: 2 }}>
          {/* Add percentage adjustment section */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 2, 
            mb: 3, 
            bgcolor: 'background.paper',
            p: 2,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Ajuste porcentual"
                type="number"
                size="small"
                value={percentageAdjustment}
                onChange={(e) => setPercentageAdjustment(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={{ width: '150px' }}
              />
              <Button 
                variant="contained"
                onClick={handlePercentageAdjustment}
                disabled={!percentageAdjustment || isNaN(percentageAdjustment)}
                size="small"
              >
                Aplicar ajuste
              </Button>
            </Box>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeBasePrice}
                  onChange={(e) => setIncludeBasePrice(e.target.checked)}
                  size="small"
                />
              }
              label="Incluir precio base en el ajuste"
              sx={{ ml: 0 }}
            />
          </Box>

          {/* Existing price breaks section */}
          {priceBreaks.map((pb, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                label="Cantidad mínima"
                type="number"
                value={pb.minQuantity}
                onChange={(e) => handlePriceBreakChange(index, 'minQuantity', e.target.value)}
                size="small"
                sx={{ width: '40%' }}
              />
              <TextField
                label="Precio"
                type="number"
                value={pb.price}
                onChange={(e) => handlePriceBreakChange(index, 'price', e.target.value)}
                size="small"
                sx={{ width: '40%' }}
              />
              <IconButton 
                onClick={() => handleRemovePriceBreak(index)}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button 
            variant="outlined" 
            onClick={handleAddPriceBreak}
            startIcon={<AddIcon />}
            sx={{ mt: 1 }}
          >
            Agregar descuento
          </Button>
        </Box>
      );
    }

    return (
      <TextField
        autoFocus
        margin="dense"
        fullWidth
        multiline={['description'].includes(field)}
        rows={['description'].includes(field) ? 4 : 1}
        type={['basePrice', 'availableAmount'].includes(field) ? 'number' : 'text'}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
      />
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {field === 'priceBreaks' ? 'Editar descuentos por volumen' : `Editar ${field}`}
      </DialogTitle>
      <DialogContent>
        {renderEditContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSave}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
};

const ShippingFeesSection = ({ product, isDistributor, onUpdate }) => {
  const [editingZone, setEditingZone] = useState(null);
  const [editValues, setEditValues] = useState({});
  const theme = useTheme();
  const handleEdit = (zoneFee) => {
    setEditingZone(zoneFee.zone);
    setEditValues({
      base: zoneFee.fees.base,
      pricePerUnit: zoneFee.fees.pricePerUnit
    });
  };

  const handleSave = async (zone) => {
    try {
      const updatedFees = product.shippingFees.map(fee => 
        fee.zone === zone ? {
          ...fee,
          fees: {
            ...fee.fees,
            base: Number(editValues.base),
            pricePerUnit: Number(editValues.pricePerUnit)
          }
        } : fee
      );

      const response = await instance.put(`/api/products/${product._id}/shipping-fees`, {
        shippingFees: updatedFees,
        shippingUnit: product.shippingUnit
      });

      onUpdate(response.data);
      setEditingZone(null);
    } catch (error) {
      console.error('Error updating shipping fees:', error);
      alert('Error al actualizar las tarifas de envío');
    }
  };

  return (
    <Box sx={{ mt: 3 ,
      backgroundColor: {xs:'rgba(255, 255, 255, 0.95)'},
      borderRadius: {xs:'4px', md:'4px'},
      padding: {xs:'10px', md:'20px'},
      border: '1px solid #e6e6e6',


    }}>
      <Typography variant="h6" gutterBottom>
        Zonas de Envío y Tarifas
      </Typography>
      <Grid container spacing={2}>
        {product.shippingFees.map((zoneFee) => (
          <Grid item xs={12} sm={6} md={4} key={zoneFee.zone}>
            <Box 
              sx={{ 
                p: 2,
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: isDistributor ? 'translateY(-4px)' : 'none',
                  boxShadow: isDistributor ? 4 : 2
                },
                border: '1px solid #e6e6e6',
                borderRadius: '8px'
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {zoneFee.zone}
                </Typography>
              </Box>

              {editingZone === zoneFee.zone ? (
                // Edit Mode
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    size="small"
                    label="Tarifa Base"
                    type="number"
                    value={editValues.base}
                    onChange={(e) => setEditValues({ ...editValues, base: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                  />
                  <TextField
                    size="small"
                    label={`Precio por ${UNIT_LABELS_SINGULAR[zoneFee.fees.unit] || 'unidad'}`}
                    type="number"
                    value={editValues.pricePerUnit}
                    onChange={(e) => setEditValues({ ...editValues, pricePerUnit: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button 
                      size="small" 
                      variant="contained" 
                      onClick={() => handleSave(zoneFee.zone)}
                    >
                      Guardar
                    </Button>
                    <Button 
                      size="small" 
                      onClick={() => setEditingZone(null)}
                    >
                      Cancelar
                    </Button>
                  </Box>
                </Box>
              ) : (
                // Display Mode
                <>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Tarifa base: <Box component="span" sx={{ fontWeight: 'bold' }}>${zoneFee.fees.base}</Box>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Precio por {UNIT_LABELS_SINGULAR[zoneFee.fees.unit] || 'unidad'}:{' '}
                    <Box component="span" sx={{ fontWeight: 'bold' }}>${zoneFee.fees.pricePerUnit}</Box>
                  </Typography>
                  {isDistributor && (
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(zoneFee)}
                      sx={{ mt: 2 }}
                    >
                      Editar
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};


const GuestInfoDialog = ({ open, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    email: ''
  });
  const [errors, setErrors] = useState({});

  const handleSubmit = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'El nombre es requerido';
    if (!formData.lastName) newErrors.lastName = 'El apellido es requerido';
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ 
        bgcolor: '#ff5833', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <PersonIcon /> Ingresa tus datos para continuar
      </DialogTitle>
      <DialogContent sx={{ mt: 3, px: 3 }}>
        <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
          Para acceder a la información de contacto, necesitamos algunos datos básicos.
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 2.5
        }}>
          <TextField
            label="Nombre"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            variant="outlined"
            size="medium"
          />
          <TextField
            label="Apellido"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            error={!!errors.lastName}
            helperText={errors.lastName}
            fullWidth
            variant="outlined"
            size="medium"
          />
          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            variant="outlined"
            size="medium"
          />
        </Box>
        
        <Typography textAlign={'center'} sx={{ mt: 1 }}>
          ó
        </Typography>

        <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            <Link href="/login" sx={{ fontWeight: 'bold', color: '#ff5833', textDecoration: 'underline' }}>
              Ingresa
            </Link>
            <Typography variant="body1" align="center" color="text.secondary">|</Typography>
            <Link href="/signup" sx={{ fontWeight: 'bold', color: '#ff5833', textDecoration: 'underline' }}>
              Regístrate
            </Link>
          </Box>
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          sx={{ 
            bgcolor: '#ff5833',
            '&:hover': {
              bgcolor: '#e54a2b'
            }
          }}
        >
          Continuar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ImageManager = ({ product, onUpdate, setErrorDialog, setSuccessDialog }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, imageUrl: null });

  const handleFileSelect = async (event) => {
    try {
      setUploading(true);
      const files = Array.from(event.target.files);
      
      const formData = new FormData();
      files.forEach(file => {
        formData.append('images', file);
      });

      const response = await instance.post(`/api/products/${product._id}/images`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      onUpdate(response.data);
      setSuccessDialog({
        open: true,
        message: 'Imágenes subidas exitosamente'
      });
    } catch (error) {
      console.error('Error uploading images:', error);
      setErrorDialog({
        open: true,
        message: 'Error al subir las imágenes. Por favor, inténtalo de nuevo.'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await instance.delete(`/api/products/${product._id}/images`, {
        data: { imageUrl: deleteDialog.imageUrl }
      });
      onUpdate(response.data);
      setSuccessDialog({
        open: true,
        message: 'Imagen eliminada exitosamente'
      });
      setDeleteDialog({ open: false, imageUrl: null });
    } catch (error) {
      console.error('Error deleting image:', error);
      setErrorDialog({
        open: true,
        message: 'Error al eliminar la imagen. Por favor, inténtalo de nuevo.'
      });
    }
  };

  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      <input
        type="file"
        multiple
        accept="image/*"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileSelect}
      />
      
      <Box 
        sx={{
          height: { xs: '280px', md: '460px' },
          overflow: 'hidden',
          position: 'relative',
          borderRadius: 1,
          mb: 1,
          backgroundColor: 'white',
          border: '1px solid rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          component="img"
          src={product.imageUrls?.[0] ? product.imageUrls?.[0] : LdPlaceHolder}
          alt={product.name}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            padding: 1
          }}
        />
      </Box>

      <Button
        variant="outlined"
        startIcon={<AddPhotoAlternateIcon />}
        onClick={() => fileInputRef.current.click()}
        disabled={uploading}
        size="small"
        sx={{ mb: 1 }}
      >
        {uploading ? 'Subiendo...' : 'Agregar imágenes'}
      </Button>

      <Box sx={{ 
        display: 'flex',
        gap: 1,
        flexWrap: 'wrap',
        overflowX: 'auto',
        pb: 1
      }}>
        {product.imageUrls?.map((url, index) => (
          <Box
            key={url}
            elevation={3}
            sx={{
              position: 'relative',
              width: 80,
              height: 80,
              overflow: 'hidden',
              borderRadius: 1,
              border: '1px solid rgba(0,0,0,0.08)'
            }}
          >
            <IconButton
              size="small"
              onClick={() => setDeleteDialog({ open: true, imageUrl: url })}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.7)'
                }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            <Box
              component="img"
              src={url}
              alt={`Product ${index + 1}`}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </Box>
        ))}
      </Box>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, imageUrl: null })}
      >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro que deseas eliminar esta imagen? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog({ open: false, imageUrl: null })}
            color="primary"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const userType = localStorage.getItem('userType');
  const [isDistributor, setIsDistributor] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editField, setEditField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isFavorited, setIsFavorited] = useState(false);
  const [distributorContact, setDistributorContact] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState({ email: false, phone: false });
  const [productStatus, setProductStatus] = useState(product?.status || 'active');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [guestInfo, setGuestInfo] = useState({
    name: '',
    lastName: '',
    email: ''
  });
  const [errorDialog, setErrorDialog] = useState({ open: false, message: '' });
  const [successDialog, setSuccessDialog] = useState({ open: false, message: '' });
  const [featureErrorDialog, setFeatureErrorDialog] = useState({
    open: false,
    title: '',
    message: ''
  });
  const [showCopyDialog, setShowCopyDialog] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await instance.get(`/api/products/${id}`);
        setProduct(response.data);
      
        // Check if current user is the distributor who owns this product
        const currentUser = auth.getUser();
        if (currentUser && auth.isDistributor()) {
          setIsDistributor(currentUser._id === response.data.distributor);
        } else if (currentUser && auth.getUserType() == 'subUser' && auth.getUser().permissions.canEditProducts) {
          setIsDistributor(currentUser.distributorId === response.data.distributor);
        }

      } catch (error) {
        // Detailed error logging
        console.error('Full error:', error);
        console.error('Error response:', error.response);
        console.error('Error status:', error.response?.status);
        console.error('Error message:', error.message);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (auth.isBusiness()) {
        try {
          const response = await instance.get('/api/favorites');
          const favorites = response.data;
          setIsFavorited(favorites.some(fav => fav.product._id === id));
        } catch (error) {
          console.error('Error checking favorite status:', error);
        }
      }
    };
    
    checkFavoriteStatus();
  }, [id]);

  const handleMessageClick = async () => {
    try {
      await instance.post(`/api/products/${id}/message-click`);
    } catch (error) {
      console.error('Error clicking message:', error);
    }
  };

  const handleCallClick = async () => {
    try {
      await instance.post(`/api/products/${id}/call-click`);
    } catch (error) {
      console.error('Error clicking call:', error);
    }
  };

  const handleFavoriteToggle = async () => {
    try {
      if (isFavorited) {
        await instance.delete(`/api/favorites/${id}`);
        setIsFavorited(false);
      } else {
        await instance.post(`/api/favorites/${id}`);
        setIsFavorited(true);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleEdit = (field, currentValue) => {
    setEditField(field);
    setEditValue(currentValue || '');
    setEditDialogOpen(true);
  };

  const EditableField = ({ label, value, field, isDistributor }) => {
    // If it's the title (name)
    if (field === 'name') {
      return (
        <Typography 
          variant={isMobile ? 'h5' : 'h4'}
          sx={{ 
            color: '#1a1a1a',
            fontWeight: 500,
            mb: 2,
            maxWidth: {xs: '80%', sm: 'auto'}
          }}
        >
          {value}
          {isDistributor && (
            <Tooltip title={`Editar ${label.toLowerCase()}`}>
              <IconButton size="small" onClick={() => handleEdit(field, value)} sx={{ ml: 1 }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
      );
    }

    // If it's the description
    if (field === 'description') {
      return (
        <Box sx={{ mb: 2 }}>
          {isDistributor && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {label}
              </Typography>
              <Tooltip title={`Editar ${label.toLowerCase()}`}>
                <IconButton size="small" onClick={() => handleEdit(field, value)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <Typography variant="body1" color="text.secondary" sx={{
            fontSize: '0.8rem'
          }}>
            {value || `No tiene ${label.toLowerCase()}`}
          </Typography>
        </Box>
      );
    }

    // For other fields (like basePrice, etc)
    return (
      <Box sx={{ mb: 2 }}>
        {isDistributor ? (
          // Owner view with label and edit button
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {label}
            </Typography>
            <Tooltip title={`Editar ${label.toLowerCase()}`}>
              <IconButton size="small" onClick={() => handleEdit(field, value)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}
        <Typography variant="body1" color="text.secondary">
          {value || `No tiene ${label.toLowerCase()}`}
        </Typography>
      </Box>
    );
  };

  useEffect(() => {
    const fetchDistributorContact = async () => {
      if (product?.distributor) {
        try {
          const response = await instance.get(`/api/distributors/${product.distributor}/contact`);
          setDistributorContact(response.data);
        } catch (error) {
          console.error('Error fetching distributor contact:', error);
        }
      }
    };

    fetchDistributorContact();
  }, [product?.distributor]);

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      await instance.post(`/api/products/${id}/${type}-copy`);
      setCopyFeedback(prev => ({ ...prev, [type]: true }));
      setTimeout(() => {
        setCopyFeedback(prev => ({ ...prev, [type]: false }));
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await instance.delete(`/api/admin-management/products/${id}`);
      setDeleteDialogOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error al eliminar el producto');
    }
  };

  const handleToggleStatus = async () => {
    try {
      const response = await instance.patch(`/api/moderator-management/products/${id}/toggle-status`);
      setProductStatus(response.data.product.status);
    } catch (error) {
      console.error('Error toggling product status:', error);
      alert('Error al cambiar el estado del producto');
    }
  };

  const handleFeatureProduct = async () => {
    try {
      const response = await instance.patch(`/api/products/${id}/featured`);
      setProduct(prev => ({
        ...prev,
        isFeatured: response.data.isFeatured
      }));
    } catch (error) {
      const errorMessage = error.response?.data?.message;
      
      if (errorMessage) {
        let dialogTitle = 'Error';
        let dialogMessage = errorMessage;

        if (errorMessage.includes('suscripción')) {
          dialogTitle = 'Suscripción Requerida';
          dialogMessage = 'Necesitas una suscripción activa para destacar productos. ¿Deseas ver nuestros planes?';
        } else if (errorMessage.includes('productos destacados')) {
          dialogTitle = 'Plan Insuficiente';
          dialogMessage = 'Tu plan actual no permite destacar productos o has alcanzado el límite. Actualiza a un plan superior para acceder a esta función.';
        }

        setFeatureErrorDialog({
          open: true,
          title: dialogTitle,
          message: dialogMessage
        });
      } else {
        setFeatureErrorDialog({
          open: true,
          title: 'Error',
          message: 'Ha ocurrido un error al destacar el producto'
        });
      }
    }
  };

  const handleShare = async () => {
    try {
      if (isMobile && navigator.share) {
        // Use Web Share API if available
        await navigator.share({
          title: product.name,
          text: `Mira este producto en Liderdist: ${product.name}`,
          url: window.location.href
        });
      } else {
        // Fallback to clipboard copy
        await navigator.clipboard.writeText(window.location.href);
        setShowCopyDialog(true);
        setTimeout(() => setShowCopyDialog(false), 1500); // Auto-hide after 1.5 seconds
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  if (loading) return <LoadingScreen message="Cargando detalles del producto..." />;

  if (!product) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <Typography>No se encontró el producto</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2, width:'100vw',
      backgroundColor: {xs:'#f3f3f3', md: theme.palette.background.default}
     , px: {xs:0, md:'7.5%'}
     }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" color="text.primary" sx={{ fontSize: {xs:'0.9rem', md:'1rem'},ml:{xs:1,md:0} }}>
          {product.category ? (
            <>
              {getCategoryPath(product.category)
                .map((cat, index, array) => (
                  <React.Fragment key={cat}>
                    <Box
                      component="span"
                      onClick={() => navigate(`/s/?category=${cat}`)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {CATEGORY_HIERARCHY[cat]?.name || cat}
                    </Box>
                    <Box component="span" sx={{ mx: 1, color: 'text.secondary' }}>
                      {">"}
                    </Box>
                  </React.Fragment>
                ))}
              <Box component="span" sx={{ color: 'text.primary' }}>
                {product.name}
              </Box>
            </>
          ) : (
            <>
              <Box
                component="span"
                onClick={() => navigate('/')}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Productos
              </Box>
              <Box component="span" sx={{ mx: 1, color: 'text.secondary' }}>{">"}</Box>
              <Box component="span" sx={{ color: 'text.primary' }}>
                {product.name}
              </Box>
            </>
          )}
        </Typography>
      </Box>

      <Box sx={{ 
        maxHeight: { xs: 'auto', md: 'auto' },
        width: {xs:'100vw', md:'auto'},
        display: 'flex',
        flexDirection: 'column',
        padding: 1.5,
        borderRadius: 1,
        borderColor: 'rgba(0,0,0,0.1)',
        borderWidth: '1px',
        borderStyle: 'solid',
        mb: 1.5,
        backgroundColor: 'white',
        position: 'relative'
      }}>
        <Grid container spacing={1}>
          {/* Image column */}
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 0.5 }}>
              {isDistributor ? (
                <ImageManager 
                  product={product}
                  onUpdate={setProduct}
                  setErrorDialog={setErrorDialog}
                  setSuccessDialog={setSuccessDialog}
                />
              ) : (
                <>
                  {/* Main image */}
                  <Box 
                    sx={{
                      height: { xs: '280px', md: '460px' },
                      overflow: 'hidden',
                      position: 'relative',
                      borderRadius: 1,
                      border: '1px solid rgba(0,0,0,0.08)',
                      mb: 1,
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box
                      component="img"
                      src={product.imageUrls?.[currentImageIndex] ? product.imageUrls?.[currentImageIndex] : LdPlaceHolder}
                      alt={product.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        padding: 1
                      }}
                    />
                     {product.isFeatured && (
                  <Chip
                    label="Producto Destacado"
                    sx={{
                      backgroundColor: 'orange',
                      color: 'white',
                      fontWeight: '600',
                      position: 'absolute',
                      width: '100%',
                      top: '0',
                      right: '0',
                      fontSize: '0.8rem',
                      borderRadius: '0',
                    }}
                  />
                )}
                  </Box>

                  {/* Thumbnail slider */}
                  {product.imageUrls?.length > 1 && (
                    <Box sx={{ 
                      display: 'flex',
                      gap: 1,
                      overflowX: 'auto',
                      pb: 1
                    }}>
                      {product.imageUrls.map((url, index) => (
                        <Box
                          key={url}
                          elevation={currentImageIndex === index ? 3 : 1}
                          sx={{
                            width: 60,
                            height: 60,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'scale(1.05)'
                            },
                            border: '1px solid rgba(0,0,0,0.08)',
                            borderRadius: 1,
                          }}
                          onClick={() => setCurrentImageIndex(index)}
                        >
                          <Box
                            component="img"
                            src={url}
                            alt={`${product.name} - ${index + 1}`}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Grid>

          {/* Main info column */}
          <Grid item xs={12} md={8}>
            <Box sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              borderRadius: 1,
              p: 1.5,
              position: 'relative'
            }}>
              {/* Action buttons - more compact */}
              <Box sx={{ 
                position: 'absolute',
                top: 12,
                right: 12,
                display: 'flex',
                gap: 0.5,
                zIndex: 1
              }}>
                <Tooltip title="Compartir">
                  <IconButton
                    onClick={handleShare}
                  >
                    <ShareIcon />
                  </IconButton>
                </Tooltip>
                
                {auth.isBusiness() && (
                  <Tooltip title={isFavorited ? "Quitar de favoritos" : "Agregar a favoritos"}>
                    <IconButton
                      onClick={handleFavoriteToggle}
                      color={isFavorited ? "error" : "default"}
                      sx={{ 
                        backgroundColor: 'white',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                        boxShadow: 1
                      }}
                    >
                      <FavoriteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Box sx={{ mb: 0.5,
               
              }}>
                <Typography variant="subtitle1" color="text.secondary" sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap',
                  alignItems: 'center', 
                  maxWidth: '80%',
                  gap: 0.5,
                  fontSize: '0.875rem'
                }}>
                  <Box sx={{display:'flex', alignItems:'center', gap:0.5}}>
                  <Box 
                    component="span" 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                    onClick={() => navigate(`/distributor/${product.distributor}`)}
                  >
                    {product.distributorName || 'Sin distribuidor'}
                  </Box>
                  {product.distributorVerified && (
                  <Tooltip title="Este distribuidor ha sido verificado por Liderdist." arrow placement="top">
                    <Box sx={{

                      zIndex: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '50%',
                      padding: '2px',
                      width: {xs: '16px', md:'auto'},
                      height: {xs: '16px', md:'auto'},
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <VerifiedIcon color="primary" sx={{ fontSize: '14px' }} />
                    </Box>
                  </Tooltip>
                )}
                </Box>
                  {!isMobile && ' • '}
                  <span>Publicado {moment(product.createdAt).fromNow()}</span>
                 
                  {!isMobile && <Typography component="span" color="green">
                    {product.availableAmount ? (
                      <>
                        {!isMobile && ' • '}
                        <Box component="span" sx={{ color: 'success.main' }}>
                          {product.availableAmount > 0 ? 'Disponible' : 'Agotado'}
                        </Box>
                      </>
                    ) : null}
                  </Typography>
                  }
                </Typography>
              </Box>

              <EditableField 
                type="title"
                label="Nombre" 
                value={product.name} 
                field="name" 
                isDistributor={isDistributor}
              />
              {(product.isFeatured || isDistributor) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {isDistributor && (
                  <Tooltip title={product.isFeatured ? "Quitar destacado" : "Marcar como destacado"}>
                    <IconButton
                      size="small"
                      onClick={handleFeatureProduct}
                      sx={{
                        color: product.isFeatured ? '#FFD700' : 'action.disabled',
                        '&:hover': {
                          color: product.isFeatured ? '#B7995B' : '#FFD700'
                        }
                      }}
                    >
                      <StarIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>  
              )}

              <Box sx={{ mb: 2 }}>
                { product.hasPriceBreaks && <Typography variant="h6" sx={{ 
                  mb: 1.5,
                  fontWeight: 500
                }}>
                  Precios por cantidad
                </Typography>}

                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}>
                  {/* Volume Pricing First */}
                  {product.priceBreaks?.length > 0 && (
                    <>
                      <Box sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1
                      }}>
                        {[...product.priceBreaks]
                          .sort((a, b) => a.price - b.price) // Sort by price ascending (cheapest first)
                          .map((priceBreak, index) => {
                            const isLowestPrice = index === 0; // First item is cheapest
                            return (
                              <Paper
                                key={index}
                                elevation={0}
                                sx={{
                                  p: 1.5,
                                  border: '1px solid rgba(0, 0, 0, 0.08)',
                                  borderRadius: 1,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  transition: 'all 0.2s ease',
                                  position: 'relative',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.01)',
                                    borderColor: 'rgba(0, 0, 0, 0.12)'
                                  }
                                }}
                              >
                                {isLowestPrice && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: -10,
                                      right: 16,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      backgroundColor: '#ff5833',
                                      color: 'white',
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold',
                                      boxShadow: 1,
                                    }}
                                  >
                                    <StarIcon sx={{ fontSize: '0.875rem' }} />
                                    ¡Mejor precio!
                                  </Box>
                                )}
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    Desde {priceBreak.minQuantity.toLocaleString()} {UNIT_LABELS_PLURAL[product.unit]}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {Math.round(((product.basePrice - priceBreak.price) / product.basePrice) * 100)}% de descuento sobre precio base
                                  </Typography>
                                </Box>
                                <Box display="flex" flexDirection="row" alignItems="center" gap={0.5}>
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                  ${priceBreak.price.toLocaleString()}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                por {product.unit ? UNIT_LABELS_SINGULAR[product.unit] : 'por unidad'}
                                </Typography>
                                </Box>
                              </Paper>
                            );
                          })}
                      </Box>

                      {isDistributor && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleEdit('priceBreaks', product.priceBreaks)}
                          >
                            Editar precios por volumen
                          </Button>
                        </Box>
                      )}
                    </>
                  )}

                  {/* Base Price Last */}
                  <Box sx={{ 
                    p: `${product.hasPriceBreaks ? '12px' : 0}`,
                    borderTop: `${product.hasPriceBreaks ? '1px solid rgba(0, 0, 0, 0.08)' : 'none'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: `${product.hasPriceBreaks ? '16px' : 0}`
                  }}>
                    {product.hasPriceBreaks && <Typography variant="body1" color="text.secondary">
                      Precio unitario base por {UNIT_LABELS_SINGULAR[product.unit]}
                    </Typography>}

                    {!product.hasPriceBreaks && 
                    <>
                    <Typography variant="h3" color="text.secondary" sx={{
                      fontWeight: 700,
                      color: 'primary.main'

                    }}>
                      ${product.basePrice?.toLocaleString()}
                    </Typography>
                    </>
                    }
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                     {product.hasPriceBreaks && <Typography variant="h6">
                        ${product.basePrice?.toLocaleString()}
                      </Typography>}
                      {isDistributor && (
                        <Tooltip title="Editar precio base">
                          <IconButton size="small" onClick={() => handleEdit('basePrice', product.basePrice)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
              {product.qualities && Object.keys(product.qualities).length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ 
                    mb: 1.5,
                    fontWeight: 500
                  }}>
                    Características
                  </Typography>
                  <Grid container spacing={1}>
                    {Object.entries(product.qualities).map(([key, value]) => (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <Paper 
                          elevation={0}
                          sx={{ 
                            p: 1.5,
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            borderRadius: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            height: '100%',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.01)',
                              borderColor: 'rgba(0, 0, 0, 0.12)'
                            }
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              fontWeight: 500,
                              textTransform: 'capitalize'
                            }}
                          >
                            {getQualitiesForCategory(product.category)?.[key]?.label || key}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {value}
                            {getQualitiesForCategory(product.category)?.[key]?.format && 
                              ` ${getQualitiesForCategory(product.category)[key].format}`}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              {isDistributor && (
                <>
                  <EditableField 
                    label="Cantidad Disponible" 
                    value={`${product.availableAmount} disponibles`} 
                    field="availableAmount"
                    isDistributor={isDistributor}
                  > </EditableField>

                </>
              )}

              {distributorContact && (
                <Box sx={{ 
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  borderRadius: 1,
                  p: 1.5,
                  position: 'relative',
                  minHeight: 'auto', // Dynamic height
                  transition: 'min-height 0.3s ease', // Smooth transition
                }}>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: {xs: 'column', sm: 'row'},
                    gap: 2,
                    opacity: 1, // Hide content when cover is shown
                    transition: 'opacity 0.3s ease',
                  }}>
                    {distributorContact.email && (
                      <Box sx={{ 
                        flex: 1,
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.02)'
                      }}>
                        <EmailIcon sx={{ color: '#EA4335' }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" color="text.secondary">Email</Typography>
                          <Typography variant="body1">{distributorContact.email}</Typography>
                        </Box>
                        <Tooltip title={copyFeedback.email ? "¡Copiado!" : "Copiar email"}>
                          <IconButton size="small" onClick={() => handleCopy(distributorContact.email, 'email')}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}

                    {distributorContact.phone && (
                      <Box sx={{ 
                        flex: 1,
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 1,
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.02)'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <PhoneIcon sx={{ color: '#25D366' }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">Teléfono</Typography>
                            <Typography variant="body1">{distributorContact.phone}</Typography>
                          </Box>
                          <Tooltip title={copyFeedback.phone ? "¡Copiado!" : "Copiar teléfono"}>
                            <IconButton size="small" onClick={() => handleCopy(distributorContact.phone, 'number')}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PhoneIcon />}
                            onClick={() => handleCallClick()}
                            href={`tel:${distributorContact.phone}`}
                            sx={{ flex: 1 }}
                          >
                            Llamar
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<WhatsAppIcon />}
                            onClick={() => handleMessageClick()}
                            href={`https://wa.me/549${distributorContact.phone}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                              flex: 1,
                              backgroundColor: '#25D366',
                              '&:hover': {
                                backgroundColor: '#128C7E'
                              }
                            }}
                          >
                            WhatsApp
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              {!isDistributor && (<Typography variant="h6" sx={{ mt: 4 }}>Descripción</Typography>)}

               <EditableField 
                type="subtitle"
                label="Descripción" 
                value={product.description}
                field="description"
                multiline 
                isDistributor={isDistributor}
                sx={{ mt: 4 }}
              />

            </Box>
          </Grid>
        </Grid>
      </Box>

      {!product.hideShippingFees && <Box sx={{ mb: 4 }}>
        <ShippingFeesSection 
          product={product}
          isDistributor={isDistributor}
          onUpdate={(updatedProduct) => setProduct(updatedProduct)}
        />
      </Box>}

      <EditDialog 
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        field={editField}
        value={editValue}
        productId={id}
        onUpdate={(updatedProduct) => setProduct(updatedProduct)}
      />

      {(auth.isAdmin() || (auth.isModerator() && auth.hasPermission('canManageProducts'))) && (
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          mb: 2,
          justifyContent: 'flex-end' 
        }}>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              size="small"
            >
              Eliminar Producto
            </Button>

        </Box>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"¿Eliminar producto?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Esta acción eliminará permanentemente el producto "{product.name}". 
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            color="primary"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleDelete}
            color="error"
            variant="contained"
            autoFocus
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <GuestInfoDialog
        open={showLoginDialog}
        onClose={() => setShowLoginDialog(false)}
        onSubmit={(data) => {
          setGuestInfo(data);
          setShowLoginDialog(false);
        }}
      />

      <Dialog
        open={errorDialog.open}
        onClose={() => setErrorDialog({ open: false, message: '' })}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Error"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {errorDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setErrorDialog({ open: false, message: '' })}
            color="primary"
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={successDialog.open}
        onClose={() => setSuccessDialog({ open: false, message: '' })}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Éxito"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {successDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSuccessDialog({ open: false, message: '' })}
            color="primary"
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={featureErrorDialog.open} 
        onClose={() => setFeatureErrorDialog({ ...featureErrorDialog, open: false })}
      >
        <DialogTitle sx={{ 
          bgcolor: 'error.main', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <WarningIcon />
          {featureErrorDialog.title}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>
            {featureErrorDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          {(featureErrorDialog.title === 'Suscripción Requerida' || 
              featureErrorDialog.title === 'Plan Insuficiente') && (
              <Button 
                onClick={() => {
                  setFeatureErrorDialog({ ...featureErrorDialog, open: false });
                  navigate('/pricing');
                }} 
                variant="contained"
                color="primary"
              >
                Ver Planes
              </Button>
          )}
          <Button 
            onClick={() => setFeatureErrorDialog({ ...featureErrorDialog, open: false })} 
            color="primary"
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick notification dialog */}
      <Dialog
        open={showCopyDialog}
        TransitionComponent={Fade}
        transitionDuration={300}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            minWidth: 'auto',
            p: 1,
            borderRadius: 2,
          }
        }}
        sx={{
          '& .MuiDialog-paper': {
            boxShadow: 'none',
          }
        }}
      >
        <DialogContent sx={{ p: 1 }}>
          <Typography>Link copiado</Typography>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ProductPage; 